import {
    Alert,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Link,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { AuthenticationDetails, CognitoUser } from "amazon-cognito-identity-js";
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { userPool } from "../auth/userPool";
import { useAuth } from "../contexts/AuthContext";
import { markTemplateReloginGateIfRedirectIsTemplates } from "../templateReloginGate";

const loginLabel = "ユーザー名";
const loginInputType = "text";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuthenticatedSession, signOut } = useAuth();
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [newPasswordRequired, setNewPasswordRequired] = useState(false);
  const [newPasswordRequiredUser, setNewPasswordRequiredUser] = useState(null); // CognitiveUserオブジェクト
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [showPasswordResetForm, setShowPasswordResetForm] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [feedback, setFeedback] = useState(null);

  const showFeedback = (severity, message) => {
    setFeedback({ severity, message });
  };

  const buildRedirectUrl = () => {
    const searchParams = new URLSearchParams(location.search);
    const redirectPath = searchParams.get("redirect") || "/";
    const preservedParams = new URLSearchParams();

    searchParams.forEach((value, key) => {
      if (key !== "redirect") {
        preservedParams.set(key, value);
      }
    });

    const preservedParamsString = preservedParams.toString();
    let finalRedirectUrl = redirectPath;

    if (preservedParamsString) {
      finalRedirectUrl +=
        (redirectPath.includes("?") ? "&" : "?") + preservedParamsString;
    }

    return finalRedirectUrl;
  };

  const handleAuthorizedLogin = (cognitoUser, session) => {
    setAuthenticatedSession(cognitoUser, session);
    markTemplateReloginGateIfRedirectIsTemplates(location.search);
    const finalRedirectUrl = buildRedirectUrl();
    console.log(`Redirecting to: ${finalRedirectUrl}`);
    navigate(finalRedirectUrl, { replace: true });
  };

  const handleUnauthorizedLogin = (error) => {
    signOut();
    showFeedback("error", "システムエラーです。管理者に連絡してください。");
    console.error("Login state synchronization failure", error);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const authenticationData = {
      Username: loginId,
      Password: password,
    };
    const authenticationDetails = new AuthenticationDetails(authenticationData);

    const userData = {
      Username: loginId,
      Pool: userPool,
    };
    const cognitoUser = new CognitoUser(userData);

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (session) => {
        try {
          handleAuthorizedLogin(cognitoUser, session);
        } catch (error) {
          handleUnauthorizedLogin(error);
        }
      },
      onFailure: (err) => {
        showFeedback("error", `${loginLabel}かパスワードが間違っています。`);
        console.error("Login failure", err);
      },
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        delete userAttributes.email_verified; // 不要な属性は削除
        setNewPasswordRequiredUser(cognitoUser);
        setNewPasswordRequired(true);
      },
    });
  };

  const handlePasswordReset = (event) => {
    event.preventDefault();
    const userData = {
      Username: loginId,
      Pool: userPool,
    };
    const cognitoUser = new CognitoUser(userData);

    cognitoUser.forgotPassword({
      onSuccess: (data) => {
        console.log("Password reset success", data);
        showFeedback(
          "success",
          "パスワードリセットの案内を登録済みの連絡先に送信しました。"
        );
        setShowPasswordResetForm(true);
      },
      onFailure: (err) => {
        console.error("Password reset failure", err);
        showFeedback("error", "パスワードリセットに失敗しました。");
      },
    });
  };

  const handleSetNewPassword = (event) => {
    event.preventDefault();
    // パスワードリセット確認時は、メールアドレス or ユーザー名 (loginId state) を使うのではなく、
    // 確認コードでユーザーを特定するので userData は不要
    // const userData = {
    //   Username: loginId,
    //   Pool: userPool,
    // };
    // const cognitoUser = new CognitoUser(userData);

    // forgotPassword を呼び出した cognitoUser インスタンスが必要だが、
    //現状の実装では handlePasswordReset の cognitoUser を保持していない。
    // そのため、パスワードリセットフローでは Username を指定して confirmPassword を呼ぶ必要がある。
    // loginId state にリセット対象のユーザー名/メールアドレスが入っている前提で進める。
    const cognitoUser = new CognitoUser({
      Username: loginId, // リセット対象のユーザー名/メールアドレス
      Pool: userPool,
    });

    cognitoUser.confirmPassword(verificationCode, newPassword, {
      onSuccess: () => {
        showFeedback("success", "新しいパスワードが設定されました。ログインしてください。");
        setShowPasswordResetForm(false);
        setShowPasswordReset(false);
      },
      onFailure: (err) => {
        showFeedback("error", `パスワードのリセットに失敗しました: ${err.message}`);
      },
    });
  };

  const handleNewPassword = (event) => {
    event.preventDefault();
    if (newPassword !== confirmNewPassword) {
      showFeedback("error", "パスワードが一致しません。");
      return;
    }

    if (newPassword.length < 8) {
      showFeedback("error", "パスワードは8文字以上でなければなりません。");
      return;
    }

    newPasswordRequiredUser.completeNewPasswordChallenge(newPassword, null, {
      onSuccess: (session) => {
        console.log("Password change success", session);
        try {
          handleAuthorizedLogin(newPasswordRequiredUser, session);
          showFeedback("success", "パスワードを変更しました。");
        } catch (error) {
          handleUnauthorizedLogin(error);
        }
      },
      onFailure: (err) => {
        console.error("Password change failure", err);
        showFeedback("error", "パスワードの変更に失敗しました。");
      },
    });
  };

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 64px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 4,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 520,
          p: { xs: 3, sm: 4 },
          borderRadius: 4,
        }}
      >
        <Stack spacing={2.5}>
          <Typography variant="h5">
            ログイン
          </Typography>

          {feedback ? (
            <Alert severity={feedback.severity} onClose={() => setFeedback(null)}>
              {feedback.message}
            </Alert>
          ) : null}

          {!newPasswordRequired ? (
            <>
              <form onSubmit={handleSubmit}>
                <Stack spacing={2}>
                  <TextField
                    label={loginLabel}
                    type={loginInputType}
                    value={loginId}
                    onChange={(e) => setLoginId(e.target.value)}
                  />
                  <TextField
                    label="パスワード"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <Button type="submit" variant="contained" size="large">
                    ログイン
                  </Button>
                </Stack>
              </form>

              <Link
                component="button"
                variant="body2"
                onClick={(e) => {
                  e.preventDefault();
                  setShowPasswordReset(true);
                }}
                sx={{ alignSelf: "flex-start" }}
              >
                パスワードを忘れた場合
              </Link>

              {showPasswordReset && (
                <Paper variant="outlined" sx={{ p: 2.5 }}>
                  <form onSubmit={handlePasswordReset}>
                    <Stack spacing={2}>
                      <Typography variant="subtitle1">
                        パスワードリセットの案内を送信
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ログインに使うユーザー名を入力してください。
                      </Typography>
                      <TextField
                        label={loginLabel}
                        type={loginInputType}
                        value={loginId}
                        onChange={(e) => setLoginId(e.target.value)}
                      />
                      <Button type="submit" variant="contained">
                        リセットメールを送信
                      </Button>
                    </Stack>
                  </form>
                </Paper>
              )}

              {showPasswordResetForm && (
                <Paper variant="outlined" sx={{ p: 2.5 }}>
                  <form onSubmit={handleSetNewPassword}>
                    <Stack spacing={2}>
                      <Typography variant="subtitle1">
                        確認コードの入力
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        登録済みの連絡先に送信された確認コードと新しいパスワードを入力してください。
                      </Typography>
                      <TextField
                        label="確認コード"
                        type="text"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                      />
                      <TextField
                        label="新しいパスワード"
                        type={showPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                      />
                      <TextField
                        label="新しいパスワード（確認）"
                        type={showPassword ? "text" : "password"}
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={showPassword}
                            onChange={(e) => setShowPassword(e.target.checked)}
                          />
                        }
                        label="パスワードを表示する"
                      />
                      <Button type="submit" variant="contained">
                        パスワードをリセット
                      </Button>
                    </Stack>
                  </form>
                </Paper>
              )}
            </>
          ) : (
            <>
              <Alert severity="warning">
                パスワードを変更する必要があります。8文字以上で設定してください。
              </Alert>
              <form onSubmit={handleNewPassword}>
                <Stack spacing={2}>
                  <TextField
                    label="新しいパスワード"
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <TextField
                    label="新しいパスワード（確認）"
                    type={showPassword ? "text" : "password"}
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showPassword}
                        onChange={(e) => setShowPassword(e.target.checked)}
                      />
                    }
                    label="パスワードを表示する"
                  />
                  <Button type="submit" variant="contained" size="large">
                    新しいパスワードを設定する
                  </Button>
                </Stack>
              </form>
            </>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};

export default Login;
